import { supabase } from '../lib/supabase';

/**
 * Service for checking and applying discount rules to invoices
 */


const discountService = {
  /**
   * Find applicable discount rules for an invoice
   * @param {Object} invoiceData - Invoice data including user, country, amount, etc.
   * @returns {Promise<Array>} Array of applicable discount rules
   */
  async getAvailableRulesForDisplay(invoiceData) {
  try {
    const applicableRules = await this.findApplicableDiscountRules(invoiceData);
    
    // Return rules with formatted data for UI
    return applicableRules.map(rule => ({
      id: rule.id,
      rule_name: rule.rule_name,
      rule_code: rule.rule_code,
      rule_type: rule.rule_type,
      discount_percentage: rule.discount_percentage,
      description: rule.description,
      min_order_quantity: rule.min_order_quantity,
      priority: rule.priority || 0
    }));
  } catch (error) {
    console.error('Error getting available rules:', error);
    return [];
  }
},
 /**
   * Apply a single selected discount rule to invoice
   */
  async applySingleDiscount(invoiceId, ruleId, invoiceData) {
    try {
      // Get the specific rule
      const { data: rule, error } = await supabase
        .from('discount_rules')
        .select('*')
        .eq('id', ruleId)
        .single();
 
      if (error) throw error;
      if (!rule) throw new Error('Règle de remise introuvable');
 
      // Calculate discount
      const subtotal = parseFloat(invoiceData.subtotal);
      const discountAmount = (subtotal * parseFloat(rule.discount_percentage)) / 100;
 
      // Apply discount
      const updatedInvoice = await this.applyDiscountToInvoice(invoiceId, {
        totalDiscountAmount: discountAmount,
        appliedRules: [{
          rule_id: rule.id,
          rule_name: rule.rule_name,
          rule_type: rule.rule_type || rule.discount_type,
          discount_percentage: rule.discount_percentage,
          discount_amount: discountAmount
        }]
      });
 
      return updatedInvoice;
    } catch (error) {
      console.error('Error applying single discount:', error);
      throw error;
    }
  },
  async findApplicableDiscountRules(invoiceData) {
    try {
      const { client_id, country_id, subtotal, case_id } = invoiceData;

      // Fetch all active discount rules
      const { data: allRules, error } = await supabase
        .from('discount_rules')
        .select(`
          *
        `)
        .eq('is_active', true)
        .order('priority', { ascending: true }); // Lower priority number = higher priority

      if (error) throw error;

      // Filter rules based on applicability
      const applicableRules = [];

      for (const rule of allRules || []) {
        // Check country applicability
        const ruleCountries = rule.discount_rule_countries || [];
        const ruleCountryIds = rule.country_ids || []; // Also check array field
        const hasCountryRestriction = ruleCountries.length > 0 || ruleCountryIds.length > 0;
        
        if (hasCountryRestriction) {
          const isCountryApplicable = 
            ruleCountries.some(rc => rc.country_id === country_id) ||
            ruleCountryIds.includes(country_id);
          
          if (!isCountryApplicable) {
            continue; // Skip this rule
          }
        }

        // Map database fields to expected format
        const ruleType = rule.rule_type || rule.discount_type;
        
        // Check rule type specific conditions
        let isApplicable = false;

        switch (ruleType) {
          case 'volume':
            // For volume discounts, check if we've reached min quantity
            if (case_id) {
              const totalAligners = await this.getTotalAlignersForCase(case_id);
              const minQuantity = rule.min_order_quantity || rule.min_quantity;
              if (minQuantity && totalAligners >= minQuantity) {
                isApplicable = true;
              }
            }
            break;

          case 'early_payment':
            // Early payment discounts are applied at payment time, not invoice generation
            // We'll mark it as applicable but won't apply it yet
            isApplicable = true;
            break;

          case 'loyalty':
            // Check customer's order history
            if (client_id) {
              const isLoyal = await this.checkCustomerLoyalty(client_id);
              if (isLoyal) {
                isApplicable = true;
              }
            }
            break;

          case 'seasonal':
          case 'custom':
            // These are always applicable if active and country matches
            isApplicable = true;
            break;

          default:
            isApplicable = true;
        }

        if (isApplicable) {
          // Normalize rule for consistent processing
          applicableRules.push({
            id: rule.id,
            rule_name: rule.rule_name,
            rule_type: ruleType,
            discount_percentage: rule.discount_percentage,
            priority: rule.priority || 0
          });
        }
      }

      return applicableRules;
    } catch (error) {
      console.error('Error finding applicable discount rules:', error);
      return [];
    }
  },

  /**
   * Calculate total discount amount from applicable rules
   * @param {Array} rules - Array of discount rules
   * @param {number} subtotal - Invoice subtotal
   * @returns {Object} { totalDiscountAmount, totalDiscountPercentage, appliedRules }
   */
  calculateDiscount(rules, subtotal) {
    let totalDiscountAmount = 0;
    let totalDiscountPercentage = 0;
    const appliedRules = [];

    // Sort by priority (already sorted in query, but double-check)
    const sortedRules = [...rules].sort((a, b) => (a.priority || 0) - (b.priority || 0));

    for (const rule of sortedRules) {
      const discountPercentage = rule.discount_percentage;
      
      if (discountPercentage) {
        const discountAmount = (subtotal * discountPercentage) / 100;
        
        totalDiscountAmount += discountAmount;
        totalDiscountPercentage += discountPercentage;

        appliedRules.push({
          rule_id: rule.id,
          rule_name: rule.rule_name,
          rule_type: rule.rule_type,
          discount_percentage: discountPercentage,
          discount_amount: discountAmount,
        });

        // Note: We're applying discounts additively
        // You could change this to compound or other methods
      }
    }

    // Cap total discount at 100%
    if (totalDiscountPercentage > 100) {
      totalDiscountPercentage = 100;
      totalDiscountAmount = subtotal;
    }

    return {
      totalDiscountAmount: Math.round(totalDiscountAmount * 100) / 100,
      totalDiscountPercentage: Math.round(totalDiscountPercentage * 100) / 100,
      appliedRules,
    };
  },

  /**
   * Apply discount to an invoice
   * @param {string} invoiceId - Invoice ID
   * @param {Object} discountData - Discount calculation result
   * @returns {Promise<Object>} Updated invoice
   */
  async applyDiscountToInvoice(invoiceId, discountData) {
    try {
      const { totalDiscountAmount, appliedRules } = discountData;

      // Fetch current invoice
      const { data: invoice, error: fetchError } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', invoiceId)
        .single();

      if (fetchError) throw fetchError;

      // Calculate new totals
      const subtotal = parseFloat(invoice.subtotal || 0);
      const taxAmount = parseFloat(invoice.tax_amount || 0);
      const discountAmount = parseFloat(totalDiscountAmount || 0);
      
      // New total = subtotal - discount + tax
      const newTotal = subtotal - discountAmount + taxAmount;
      const balanceDue = newTotal - parseFloat(invoice.amount_paid || 0);

      // Update invoice with discount
      const { data: updatedInvoice, error: updateError } = await supabase
        .from('invoices')
        .update({
          discount_amount: discountAmount,
          total_amount: newTotal,
          balance_due: balanceDue,
          updated_at: new Date().toISOString(),
        })
        .eq('id', invoiceId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Record applied discounts
      for (const appliedRule of appliedRules) {
        const { error: insertError } = await supabase
          .from('applied_discounts')
          .insert({
            invoice_id: invoiceId,
            discount_rule_id: appliedRule.rule_id,
            discount_amount: appliedRule.discount_amount,
            discount_percentage: appliedRule.discount_percentage,
            applied_at: new Date().toISOString(),
          });

        if (insertError) {
          console.error('Error recording applied discount:', insertError);
        }
      }

      return updatedInvoice;
    } catch (error) {
      console.error('Error applying discount to invoice:', error);
      throw error;
    }
  },

  /**
   * Get total aligners produced for a case
   * @param {string} caseId - Case ID
   * @returns {Promise<number>} Total aligners
   */
  async getTotalAlignersForCase(caseId) {
    try {
      const { data: batches, error } = await supabase
        .from('production_batches')
        .select('model_up_from, model_up_to, model_low_from, model_low_to')
        .eq('case_id', caseId)
        .eq('status', 'completed');

      if (error) throw error;

      const total = (batches || []).reduce((sum, batch) => {
        const upAligners = (batch.model_up_to - batch.model_up_from + 1) || 0;
        const lowAligners = (batch.model_low_to - batch.model_low_from + 1) || 0;
        return sum + upAligners + lowAligners;
      }, 0);

      return total;
    } catch (error) {
      console.error('Error getting total aligners:', error);
      return 0;
    }
  },

  /**
   * Check if customer qualifies for loyalty discount
   * @param {string} clientId - Client ID (user ID)
   * @returns {Promise<boolean>} Whether customer is loyal
   */
  async checkCustomerLoyalty(clientId) {
    try {
      // Get paid invoices count for this customer
      const { data: invoices, error } = await supabase
        .from('invoices')
        .select('id, status')
        .eq('client_id', clientId)
        .eq('status', 'paid');

      if (error) throw error;

      // Customer is loyal if they have 3+ paid invoices
      return (invoices || []).length >= 3;
    } catch (error) {
      console.error('Error checking loyalty:', error);
      return false;
    }
  },

  /**
   * Check and apply all applicable discounts to an invoice
   * @param {string} invoiceId - Invoice ID
   * @param {Object} invoiceData - Invoice context data
   * @returns {Promise<Object>} Updated invoice with discounts
   */
  async checkAndApplyDiscounts(invoiceId, invoiceData) {
    try {
      console.log('Checking discounts for invoice:', invoiceId);

      // Find applicable rules
      const applicableRules = await this.findApplicableDiscountRules(invoiceData);

      console.log('Found applicable rules:', applicableRules.length);

      if (applicableRules.length === 0) {
        console.log('No applicable discount rules found');
        return null;
      }

      // Calculate discount
      const discountData = this.calculateDiscount(applicableRules, invoiceData.subtotal);

      console.log('Calculated discount:', discountData);

      if (discountData.totalDiscountAmount > 0) {
        // Apply discount to invoice
        const updatedInvoice = await this.applyDiscountToInvoice(invoiceId, discountData);
        
        console.log('Discount applied successfully:', {
          invoiceId,
          discountAmount: discountData.totalDiscountAmount,
          rulesApplied: discountData.appliedRules.length,
        });

        return updatedInvoice;
      }

      return null;
    } catch (error) {
      console.error('Error in checkAndApplyDiscounts:', error);
      throw error;
    }
  },
};

export default discountService;